import { getTimezoneOffset as getIanaTimezoneOffset } from 'date-fns-tz'

/**
 * Curated IANA timezones shared by scheduler settings and notification quiet hours.
 * Union of the former TaskScheduler COMMON_TIMEZONES and notification prefs lists.
 */
export const COMMON_TIMEZONES = [
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Caracas',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Bangkok',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Kuala_Lumpur',
  'Asia/Manila',
  'Asia/Nicosia',
  'Asia/Novosibirsk',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Singapore',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Asia/Vladivostok',
  'Asia/Yekaterinburg',
  'Atlantic/Azores',
  'Atlantic/South_Georgia',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Bucharest',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kyiv',
  'Europe/London',
  'Europe/Moscow',
  'Europe/Paris',
  'Europe/Warsaw',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Chatham',
  'Pacific/Fiji',
  'Pacific/Honolulu',
  'Pacific/Kiritimati',
  'Pacific/Midway',
  'Pacific/Noumea',
  'UTC',
] as const

export type CommonTimezone = (typeof COMMON_TIMEZONES)[number]

/** Format: "Asia/Nicosia (UTC+3)" or "Asia/Kolkata (UTC+5:30)" */
export function formatTimezoneLabel(tz: string): string {
  try {
    const offsetMs = getIanaTimezoneOffset(tz, new Date())
    const totalMin = Math.round(offsetMs / 60000)
    const sign = totalMin >= 0 ? '+' : '-'
    const h = Math.floor(Math.abs(totalMin) / 60)
    const m = Math.abs(totalMin) % 60
    const offsetStr = m ? `UTC${sign}${h}:${String(m).padStart(2, '0')}` : `UTC${sign}${h}`
    return `${tz.replace(/_/g, ' ')} (${offsetStr})`
  } catch {
    return tz
  }
}

export const COMMON_TIMEZONE_OPTIONS = COMMON_TIMEZONES.map(tz => ({
  value: tz as string,
  text: formatTimezoneLabel(tz),
  label: formatTimezoneLabel(tz),
}))

/** Browser-reported IANA zone, or UTC. */
export function guessBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && typeof tz === 'string') {
      return tz
    }
  } catch {
    /* ignore */
  }
  return 'UTC'
}

function sortTimezoneIds(a: string, b: string): number {
  if (a === 'UTC') return -1
  if (b === 'UTC') return 1
  return a.localeCompare(b)
}

/** Ensure select options include the saved / guessed zone even if not in the curated list. */
export function timezoneOptionsForSelect(current?: string | null): { value: string; text: string }[] {
  const extras = [current, guessBrowserTimezone()].filter(Boolean) as string[]
  return Array.from(new Set([...(COMMON_TIMEZONES as readonly string[]), ...extras]))
    .sort(sortTimezoneIds)
    .map(tz => ({ value: tz, text: formatTimezoneLabel(tz) }))
}

/** Autocomplete-friendly options: curated list + any extras (current / browser / robot). */
export function timezoneAutocompleteOptions(...extras: Array<string | null | undefined>): {
  value: string
  label: string
}[] {
  const extraIds = extras.filter(Boolean) as string[]
  return Array.from(new Set([...(COMMON_TIMEZONES as readonly string[]), ...extraIds]))
    .sort(sortTimezoneIds)
    .map(tz => ({ value: tz, label: formatTimezoneLabel(tz) }))
}
